import * as Sentry from "@sentry/browser";
import { captureConsoleIntegration } from "@sentry/integrations";
import type { Breadcrumb, ErrorEvent, StackFrame } from "@sentry/types";

import { getAppName, getAppVersion } from "../api/appInfo";
import { error as logError } from "../logger/logger";
import { isLocalEnvironment } from "../utils/Environment";
import { isExternalError, isIgnoredMessage, isSpamError, stackFramesToText } from "./filters";
import { createRateLimiter } from "./rateLimit";
import { makeCloudTransport } from "./transport";
import {
	DEFAULT_DSN,
	DEFAULT_ENDPOINT,
	ENVIRONMENT,
	ERROR,
	type ErrorTrackingConfig,
	type ErrorTrackingUser,
	HOST_FRAGMENT,
	type Level,
	SENTRY_LEVEL,
	TAG,
	TRACKER_SENTRY,
	WARN,
} from "./types";
import { generalizeUrl } from "./urlGeneralize";

export { DEFAULT_DSN, DEFAULT_ENDPOINT, ENVIRONMENT, ERROR, TAG, WARN } from "./types";
export type {
	Environment,
	ErrorTrackingConfig,
	ErrorTrackingUser,
	ErrorTransportOptions,
	Level,
} from "./types";

const resolveEnvironment = (override?: string): string => {
	if (override) return override;
	const host = typeof window !== "undefined" ? window.location.host : "";
	if (isLocalEnvironment()) return ENVIRONMENT.TEST;
	if (host.indexOf(HOST_FRAGMENT.STAGE) >= 0) return ENVIRONMENT.STAGE;
	if (host.indexOf(HOST_FRAGMENT.TEST) >= 0) return ENVIRONMENT.TEST;
	if (host.indexOf(HOST_FRAGMENT.DEV) >= 0) return ENVIRONMENT.DEV;
	if (host.indexOf(HOST_FRAGMENT.EU) >= 0) return ENVIRONMENT.EU;
	return ENVIRONMENT.PROD;
};

// Cap breadcrumb messages so oversized log lines don't dominate the payload.
const MAX_BREADCRUMB_MESSAGE = 500;

const generalizeBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
	if (breadcrumb.data) {
		// `message` already carries the flattened log line; drop the duplicated raw data.
		delete breadcrumb.data.arguments;
		delete breadcrumb.data.logger;
		if (typeof breadcrumb.data.url === "string") {
			breadcrumb.data = { ...breadcrumb.data, url: generalizeUrl(breadcrumb.data.url) };
		}
	}
	if (
		typeof breadcrumb.message === "string" &&
		breadcrumb.message.length > MAX_BREADCRUMB_MESSAGE
	) {
		breadcrumb.message = breadcrumb.message.slice(0, MAX_BREADCRUMB_MESSAGE - 1) + "…";
	}
	return breadcrumb;
};

const generalizeFrames = (frames: StackFrame[] | undefined) => {
	frames?.forEach(frame => {
		if (frame.filename) frame.filename = generalizeUrl(frame.filename);
	});
};

const generalizeEventUrls = (event: ErrorEvent) => {
	if (event.request?.url) event.request.url = generalizeUrl(event.request.url);
	event.exception?.values?.forEach(value => generalizeFrames(value.stacktrace?.frames));
};

// Failed-request messages carry no URL, so every failing call collapses into one
// title. Append the endpoint recovered from the matching fetch/xhr breadcrumb.
const REQUEST_FAILED_PREFIX = "Request failed with status code";

const appendRequestUrlToMessage = (event: ErrorEvent) => {
	const exception = event.exception?.values?.[0];
	const message = exception?.value ?? event.message;
	if (!message || message.indexOf(REQUEST_FAILED_PREFIX) !== 0) return;

	const statusCode = Number(message.split(" ")[5]);
	if (!Number.isFinite(statusCode)) return;

	const breadcrumbs = event.breadcrumbs;
	if (!breadcrumbs?.length) return;

	// The failing request is the newest breadcrumb with a matching status code.
	for (let i = breadcrumbs.length - 1; i > -1; i--) {
		const data = breadcrumbs[i].data;
		const url = data?.url;
		if (Number(data?.status_code) === statusCode && typeof url === "string") {
			const updated = `${message} for ${generalizeUrl(url)}`;
			if (exception) {
				exception.value = updated;
			} else {
				event.message = updated;
			}
			return;
		}
	}
};

let installed = false;
let rateLimiter: ReturnType<typeof createRateLimiter> | null = null;

export const initErrorTracking = (config: ErrorTrackingConfig = {}) => {
	if (installed) return;
	installed = true;

	// Tracking is a non-critical concern; a failing init must never break app bootstrap.
	try {
		init(config);
	} catch (initError) {
		logError("Error tracking initialization failed", initError);
	}
};

const init = (config: ErrorTrackingConfig) => {
	const environment = resolveEnvironment(config.environment);
	// Resolved per event/send so `setAppName` calls made after init are picked up;
	// an explicit `config.appName` stays fixed.
	const resolveAppName = () => config.appName ?? getAppName();
	const appVersion = config.appVersion ?? getAppVersion();
	// Report in every deployed environment; local dev has no backend to receive it.
	const enabled = !isLocalEnvironment();
	rateLimiter = createRateLimiter();

	const cloudTransport = makeCloudTransport({
		endpoint: config.endpoint ?? DEFAULT_ENDPOINT,
		appName: resolveAppName,
		appVersion,
		getAuthHeaders: config.getAuthHeaders,
	});
	// Service workers can be suspended between an error and its send, so the offline
	// transport persists envelopes to IndexedDB and retries them on resume.
	const transport = config.offline
		? Sentry.makeBrowserOfflineTransport(cloudTransport)
		: cloudTransport;

	Sentry.init({
		dsn: config.dsn ?? DEFAULT_DSN,
		environment,
		release: config.release ?? appVersion,
		attachStacktrace: true,
		autoSessionTracking: false,
		// Don't beacon SDK telemetry envelopes; only real errors should reach the endpoint.
		sendClientReports: false,
		integrations: [captureConsoleIntegration({ levels: ["error"] })],
		transport,
		transportOptions: config.transportOptions,
		// App-name tags are stamped per event in `beforeSend`, not here, so they
		// reflect `setAppName` calls made after init.
		initialScope: {
			tags: {
				[TAG.TRACKER]: TRACKER_SENTRY,
			},
		},
		beforeBreadcrumb: (breadcrumb: Breadcrumb) => {
			const generalized = generalizeBreadcrumb(breadcrumb);
			return config.beforeBreadcrumb ? config.beforeBreadcrumb(generalized) : generalized;
		},
		beforeSend: (event: ErrorEvent) => {
			const exception = event.exception?.values?.[0];
			const message = exception?.value ?? event.message ?? "";

			// eslint-disable-next-line no-console
			console.log("%c<ERROR>\n" + message, "color:red");

			if (!enabled) {
				return null;
			}

			const frames = exception?.stacktrace?.frames;
			const stackText = stackFramesToText(frames);

			if (isSpamError(message, stackText, config.spamStackFragments, config.spamMessages))
				return null;

			// Classify before URL generalization: it strips scheme/origin from frame
			// filenames, which origin-based classifiers rely on.
			const isExternal = isExternalError(frames, config.isInternalFrame, config.isVendorFrame);
			if (isExternal && config.dropExternal) return null;
			const isVendor = !!config.isVendorFrame && !!frames?.some(config.isVendorFrame);

			// Consume quota only for events that passed the drop filters, so filtered
			// floods can't starve real errors.
			if (rateLimiter && !rateLimiter.consume()) return null;

			generalizeEventUrls(event);
			appendRequestUrlToMessage(event);

			const appName = resolveAppName();
			event.tags = {
				...event.tags,
				[TAG.APP_NAME]: appName,
				[TAG.CODE_SOURCE]: event.tags?.[TAG.CODE_SOURCE] || appName,
			};

			if (typeof window !== "undefined") {
				// Generalized to stay within Sentry's tag value length limit.
				event.tags = { ...event.tags, [TAG.PAGE_URL]: generalizeUrl(window.location.href) };
			}

			let downgrade = isIgnoredMessage(message, config.ignoreMessages);
			if (isExternal) {
				event.tags = { ...event.tags, [TAG.IS_EXTERNAL]: true };
				downgrade = true;
			}
			if (isVendor) {
				event.tags = { ...event.tags, [TAG.IS_VENDOR]: true };
			}

			if (downgrade) {
				event.level = SENTRY_LEVEL[WARN];
				event.tags = { ...event.tags, [TAG.LEVEL]: WARN };
			}

			return config.beforeSend ? config.beforeSend(event) : event;
		},
	});

	registerTestErrorShortcut();
};

export const setUser = (user: ErrorTrackingUser) => {
	Sentry.setUser({ id: String(user.id) });
	if (user.instanceName !== undefined) Sentry.setTag(TAG.INSTANCE_NAME, user.instanceName);
	const sessionId = user.sessionId ?? `${user.id}_${Date.now()}`;
	Sentry.setTag(TAG.SESSION_ID, sessionId);
};

// Clears the user and user-derived tags so later errors aren't misattributed.
export const clearUser = () => {
	Sentry.setUser(null);
	Sentry.setTag(TAG.INSTANCE_NAME, "");
	Sentry.setTag(TAG.SESSION_ID, "");
};

export const setErrorContext = (key: string, value: unknown) => {
	Sentry.setTag(key, value == null ? "" : String(value));
};

export const addBreadcrumb = (breadcrumb: Breadcrumb) => {
	Sentry.addBreadcrumb(breadcrumb);
};

// Optional `tags` are applied only to this event (scoped), unlike the
// persistent tags set through `setErrorContext`. Values are limited to scalars:
// tags are indexed by Sentry, so error details belong on the captured error itself.
export const trackError = (
	error: string | Error,
	level: Level = ERROR,
	tags?: Record<string, string | number | boolean>
) => {
	const sentryLevel = SENTRY_LEVEL[level];
	Sentry.withScope(scope => {
		scope.setTag(TAG.LEVEL, level);
		scope.setLevel(sentryLevel);
		Object.entries(tags ?? {}).forEach(([key, value]) => {
			scope.setTag(key, value);
		});
		if (typeof error === "string") {
			Sentry.captureMessage(error, sentryLevel);
		} else {
			Sentry.captureException(error);
		}
	});
};

/**
 * Registers a shortcut (ctrl+alt+shift+e) that reports a dummy error to exercise
 * the pipeline on demand. Exported for contexts that forward errors elsewhere
 * instead of running `initErrorTracking` themselves.
 */
let shortcutRegistered = false;

export const registerTestErrorShortcut = () => {
	if (typeof document === "undefined" || shortcutRegistered) return;
	shortcutRegistered = true;
	document.addEventListener(
		"keydown",
		event => {
			// Ignore auto-repeat while the combo is held so one press fires once.
			if (event.repeat) return;
			// Match on the physical key (`code`) — `key` is unreliable here because
			// Alt/Option turns "e" into a dead key on macOS.
			if (event.ctrlKey && event.altKey && event.shiftKey && event.code === "KeyE") {
				trackError("DummyError!", WARN);
			}
		},
		true
	);
};
